import React, { useState } from 'react';
import { AnimatePresence, motion, type PanInfo } from 'framer-motion';
import { BookOpen, ChevronDown, Lock, X } from 'lucide-react';

import type { TimelineSection } from '../api/timelineEvents';
import SpoilerInfoTooltip from '@/components/ui/SpoilerInfoTooltip';
import SpoilAccessSwitch from '@/features/reading/components/SpoilAccessSwitch';

interface TimelineBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  sections: TimelineSection[];
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  onRetry: () => void;
  theme?: 'white' | 'sepia' | 'dark';
  maxHeightVh?: number;
  bottomOffset?: string;
  disableDismiss?: boolean;
  isSpoil: boolean;
  onSpoilChange: (value: boolean) => void;
  canDisableSpoil?: boolean;
  bookId?: number;
  bookTitle?: string;
}

const TimelineBottomSheet: React.FC<TimelineBottomSheetProps> = ({
  isOpen,
  onClose,
  sections,
  isLoading,
  isError,
  errorMessage,
  onRetry,
  theme = 'white',
  maxHeightVh = 84,
  bottomOffset,
  disableDismiss = false,
  isSpoil,
  onSpoilChange,
  canDisableSpoil = false,
  bookId,
  bookTitle,
}) => {
  const [expandedEvents, setExpandedEvents] = useState<Record<number, boolean>>({});
  const [openSections, setOpenSections] = useState<Record<number, boolean>>({});
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const sectionRefs = React.useRef<Record<number, HTMLDivElement | null>>({});
  const eventRefs = React.useRef<Record<number, HTMLDivElement | null>>({});

  let lastUnlockedIndex = -1;
  for (let i = sections.length - 1; i >= 0; i -= 1) {
    if (sections[i].status === 'unlocked') {
      lastUnlockedIndex = i;
      break;
    }
  }

  const toggleEvent = (eventId: number) => {
    setExpandedEvents((prev) => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  const toggleSection = (sectionId: number) => {
    setOpenSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  React.useEffect(() => {
    if (isOpen) return;
    setExpandedEvents({});
    setOpenSections({});
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen || sections.length === 0) return;

    const openSectionIndexFromState = (() => {
      for (let i = sections.length - 1; i >= 0; i -= 1) {
        if (openSections[sections[i].section_id]) return i;
      }
      return -1;
    })();
    const fallbackIndex = lastUnlockedIndex >= 0 ? lastUnlockedIndex : 0;
    const targetSectionIndex = openSectionIndexFromState >= 0 ? openSectionIndexFromState : fallbackIndex;
    const targetSection = sections[targetSectionIndex];
    if (!targetSection) return;

    const targetEvents = targetSection.events ?? [];
    const targetEventId =
      targetEvents.length > 0
        ? [...targetEvents].reverse().find((event) => expandedEvents[event.event_id])?.event_id
        : undefined;

    const frame = requestAnimationFrame(() => {
      const targetElement = (targetEventId && eventRefs.current[targetEventId]) || sectionRefs.current[targetSection.section_id];
      targetElement?.scrollIntoView({ block: 'center', behavior: 'auto' });
    });

    return () => cancelAnimationFrame(frame);
  }, [isOpen, sections, openSections, expandedEvents, lastUnlockedIndex]);

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const shouldClose = info.offset.y > 110 || info.velocity.y > 320;
    if (shouldClose) onClose();
  };

  const sheetClasses = theme === 'dark'
    ? 'border-gray-700 bg-gray-900'
    : theme === 'sepia'
      ? 'border-[#d9c7a9] bg-[#f4ecd8]'
      : 'border-white/60 bg-[#f9f9ff]';

  const headerClasses = theme === 'dark'
    ? 'border-gray-700 bg-gray-900/95'
    : theme === 'sepia'
      ? 'border-[#d9c7a9] bg-[#f4ecd8]/95'
      : 'border-[#ecebff] bg-[#f9f9ff]/95';

  const closeBtnClasses = theme === 'dark'
    ? 'text-gray-300 hover:bg-gray-800'
    : theme === 'sepia'
      ? 'text-[#6b5333] hover:bg-[#e8dcc6]'
      : 'text-[#5d56c9] hover:bg-[#ebe9ff]';

  const isDark = theme === 'dark';
  const isSepia = theme === 'sepia';
  const titleClasses = isDark ? 'text-gray-100' : isSepia ? 'text-[#5c4a2f]' : 'text-[#2d2a57]';
  const contentCardClasses = isDark
    ? 'border-gray-700 bg-gray-800/95'
    : isSepia
      ? 'border-[#b99f74] bg-[#7a5a32]'
      : 'border-[#ecebff] bg-white/95';
  const contentTitleClasses = isDark ? 'text-white' : isSepia ? 'text-white' : 'text-[#35306a]';
  const contentSubClasses = isDark ? 'text-gray-300' : isSepia ? 'text-[#f5e7cf]' : 'text-[#5e5899]';

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[70] bg-black/35 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            onClick={disableDismiss ? undefined : onClose}
          />

          <motion.div
            className="fixed inset-x-0 bottom-0 z-[71] mx-auto w-full max-w-md"
            style={{ bottom: 0 }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 28 }}
            drag={disableDismiss ? false : 'y'}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.05, bottom: 0.55 }}
            onDragEnd={handleDragEnd}
            dir="rtl"
          >
            <div
              className={`isolate flex flex-col overflow-hidden rounded-t-[28px] border shadow-2xl ${sheetClasses}`}
              style={{ 
                maxHeight: bottomOffset
                  ? `min(${maxHeightVh}vh, calc(100dvh - var(--onboarding-header-h, 110px) - 0.5rem))`
                  : `${maxHeightVh}vh`,
                paddingBottom: bottomOffset
                  ? `calc(env(safe-area-inset-bottom) + ${bottomOffset})`
                  : 'env(safe-area-inset-bottom)'
              }}
            >
              <div className={`sticky top-0 z-20 shrink-0 border-b ${bottomOffset ? 'px-3 pb-2 pt-1.5' : 'px-4 pb-3 pt-2'} backdrop-blur ${headerClasses}`}>
                <div className={`mx-auto mb-1.5 h-1 w-12 rounded-full ${theme === 'dark' ? 'bg-gray-600' : theme === 'sepia' ? 'bg-[#cbb797]' : 'bg-[#d5d2ff]'}`} />
                <div className="relative flex flex-col items-center justify-center">
                  {!disableDismiss && <button
                    onClick={onClose}
                    className={`absolute left-0 rounded-full p-2 transition-colors ${closeBtnClasses}`}
                    aria-label="بستن"
                  >
                    <X className="h-5 w-5" />
                  </button>}
                  <h3 className={`text-center font-persian ${bottomOffset ? 'text-[15px]' : 'text-base'} font-bold ${titleClasses}`}>خط زمانی</h3>
                  {!isLoading && !isError && sections.length > 0 && (
                    <div className="mt-2 flex items-center justify-center gap-2" dir="rtl">
                    <div className="relative">
                      <span className={`font-persian text-xs font-semibold ${titleClasses}`}>لو نده</span>
                      <SpoilerInfoTooltip
                        description="روشن بودن یعنی نمایش اتفاقات بر اساس قسمت هایی هست که خوندی.با خاموش کردن نمایش اتفاقات بر اساس کل کتاب هست. حتی اون قسمت هایی که نخوندی"
                        className="absolute -top-3 -right-1"
                      />
                    </div>
                    <SpoilAccessSwitch
                      label=""
                      isSpoil={isSpoil}
                      canDisableSpoil={canDisableSpoil}
                      onSpoilChange={onSpoilChange}
                      theme={theme}
                      bookId={bookId}
                      bookTitle={bookTitle}
                      subscriptionButtonType="story_timeline"
                    />
                    </div>
                  )}
                </div>
              </div>

              <div
                ref={scrollContainerRef}
                className={`relative z-0 min-h-0 overflow-y-auto ${bottomOffset ? 'px-3 pb-0 pt-2' : 'px-4 pb-0 pt-3'}`}
                style={{
                  maxHeight: `calc(${maxHeightVh}vh - 80px)`,
                  paddingBottom: bottomOffset
                    ? '1rem'
                    : undefined,
                }}
              >
                {isLoading && (
                  <div className="space-y-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="h-16 animate-pulse rounded-2xl bg-[#ecebff]" />
                    ))}
                  </div>
                )}

                {!isLoading && isError && (
                  <div className="rounded-2xl border border-red-100 bg-white p-4 text-right" dir="rtl">
                    <p className="font-persian text-sm text-red-600">{errorMessage || 'خطا در بارگذاری خط زمانی رخ داد.'}</p>
                    <button
                      onClick={onRetry}
                      className="mt-3 rounded-xl bg-[#6a5be2] px-4 py-2 font-persian text-sm text-white"
                    >
                      تلاش مجدد
                    </button>
                  </div>
                )}

                {!isLoading && !isError && sections.length === 0 && (
                  <div className="rounded-2xl border border-[#ecebff] bg-white p-4 text-right" dir="rtl">
                    <p className="flex items-center justify-center gap-0.5 font-persian text-lg font-bold text-[#5f5a9a]" dir="rtl">
                      <span>به زودی</span>
                      {[0, 1, 2].map((index) => (
                        <motion.span
                          key={index}
                          animate={{ opacity: [0, 1, 0] }}
                          transition={{ duration: 1.4, repeat: Infinity, delay: index * 0.25 }}
                        >
                          .
                        </motion.span>
                      ))}
                    </p>
                  </div>
                )}

                {!isLoading && !isError && sections.length > 0 && (
                  <div className="mx-auto w-full max-w-[360px] space-y-2" dir="rtl">
                    {sections.map((section, sectionIndex) => {
                      const isSectionOpen = openSections[section.section_id] ?? false;
                      const isLocked = section.status !== 'unlocked';
                      const events = section.events ?? [];
                      const hasExpandedEvents = isSectionOpen && events.length > 0;
                      const hasNextSection = sectionIndex < sections.length - 1;

                      const isSectionReached = sectionIndex <= lastUnlockedIndex;
                      const nextSectionReached = hasNextSection && sectionIndex + 1 <= lastUnlockedIndex;
                      const sectionPointColor = isSectionReached
                        ? (isSepia ? 'border-[#8a5d28] bg-[#8a5d28]' : 'border-[#6f63ff] bg-[#6f63ff]')
                        : 'border-[#cfcbe6] bg-white';
                      const sectionConnectorColor = isSectionReached
                        ? (isSepia ? 'bg-[#8a5d28]' : 'bg-[#6f63ff]')
                        : 'bg-[#ddd9ff]';

                      const sectionHasIncomingLine = sectionIndex > 0;
                      const sectionIncomingReached = sectionHasIncomingLine && isSectionReached;
                      const sectionHasOutgoingLine = hasExpandedEvents || hasNextSection;
                      const sectionOutgoingReached = isSectionReached && (hasExpandedEvents ? !isLocked : nextSectionReached);

                      return (
                        <div
                          key={section.section_id}
                          className="space-y-2"
                          ref={(el) => {
                            sectionRefs.current[section.section_id] = el;
                          }}
                        >
                          <div className="grid grid-cols-[1fr_14px] items-stretch gap-0" dir="ltr">
                            <button
                              onClick={() => toggleSection(section.section_id)}
                              className={`relative z-10 mr-2 flex w-[calc(100%-8px)] justify-self-start flex-row-reverse items-center justify-between overflow-hidden rounded-2xl border px-3 py-3 text-right ${
                                isSectionReached
                                  ? (isDark ? 'border-[#5969d8] bg-gray-800' : isSepia ? 'border-[#c08b4a] bg-[#6e4f2d]' : 'border-[#dcd7ff] bg-white')
                                  : (isDark ? 'border-gray-700 bg-gray-800/70' : isSepia ? 'border-[#9e7a4d] bg-[#81603a]' : 'border-[#eceaf8] bg-[#f8f8fc]')
                              }`}
                              dir="rtl"
                            >
                              <ChevronDown
                                className={`h-4 w-4 transition-transform ${isDark || isSepia ? 'text-white' : 'text-[#6f67cc]'} ${isSectionOpen ? 'rotate-180' : ''}`}
                              />

                              <div className="mr-1 ml-3 min-w-0 flex-1">
                                <p className={`max-w-full whitespace-normal break-words text-right font-persian text-sm font-bold leading-6 ${isDark ? 'text-white' : isSepia ? 'text-white' : 'text-[#2f2b63]'}`}>{section.section_title}</p>
                                <p className={`mt-0.5 max-w-full whitespace-normal break-words text-right font-persian text-xs ${isDark ? 'text-gray-300' : isSepia ? 'text-[#f5e7cf]' : 'text-[#7b76b5]'}`}>
                                  فصل {section.chapter_number} • بخش {section.section_number}
                                </p>
                              </div>

                              <div
                                className={`ml-4 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                                  isLocked
                                    ? 'border-[#d5d3e8] bg-[#f4f4f8]'
                                    : (isDark
                                        ? 'border-[#7f8cff] bg-gradient-to-br from-[#4f63ff] to-[#3347d6]'
                                        : isSepia
                                          ? 'border-[#d6a566] bg-gradient-to-br from-[#b98741] to-[#8a5d28]'
                                          : 'border-[#8f86ff] bg-gradient-to-br from-[#7a6dff] to-[#5f54d8]')
                                }`}
                              >
                                {isLocked ? <Lock className="h-4 w-4 text-[#9996b7]" /> : <BookOpen className="h-4 w-4 text-white" />}
                              </div>
                            </button>

                            <div className="relative z-20">
                              {sectionHasIncomingLine && (
                                <div
                                  className={`absolute left-1/2 top-[-12px] bottom-1/2 w-[2px] -translate-x-1/2 ${
                                    sectionIncomingReached ? (isSepia ? 'bg-[#8a5d28]' : 'bg-[#6f63ff]') : 'bg-[#ddd9ff]'
                                  }`}
                                />
                              )}
                              {sectionHasOutgoingLine && (
                                <div
                                  className={`absolute left-1/2 top-1/2 bottom-[-12px] w-[2px] -translate-x-1/2 ${
                                    sectionOutgoingReached ? (isSepia ? 'bg-[#8a5d28]' : 'bg-[#6f63ff]') : 'bg-[#ddd9ff]'
                                  }`}
                                />
                              )}
                              <div className={`absolute left-[-8px] top-1/2 z-30 mt-[-1px] h-[2px] w-[7px] ${sectionConnectorColor}`} />
                              <div className={`absolute left-1/2 top-1/2 z-20 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 ${sectionPointColor}`} />
                            </div>
                          </div>

                          <AnimatePresence initial={false}>
                            {hasExpandedEvents && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className={`space-y-2 ${isLocked ? 'opacity-60' : ''}`} dir="rtl">
                                  {events.map((event, eventIndex) => {
                                    const isExpanded = !!expandedEvents[event.event_id];
                                    const eventReached = isSectionReached && !isLocked;
                                    const hasNextEvent = eventIndex < events.length - 1;
                                    const eventHasOutgoingLine = hasNextEvent || (!hasNextEvent && hasNextSection);
                                    const nextEventReached = hasNextEvent ? eventReached : nextSectionReached;
                                    const eventOutgoingReached = eventReached && nextEventReached;

                                    return (
                                      <motion.div
                                        key={event.event_id}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="grid grid-cols-[1fr_14px] items-stretch gap-0"
                                        dir="ltr"
                                        ref={(el) => {
                                          eventRefs.current[event.event_id] = el;
                                        }}
                                      >
                                        <div
                                          className={`relative mr-2 w-[calc(100%-8px)] justify-self-start rounded-xl border p-3 ${contentCardClasses} ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                          dir="rtl"
                                          onClick={() => !isLocked && toggleEvent(event.event_id)}
                                          role="button"
                                          tabIndex={isLocked ? -1 : 0}
                                          onKeyDown={(e) => {
                                            if (isLocked) return;
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              toggleEvent(event.event_id);
                                            }
                                          }}
                                        >
                                          <div className="relative min-h-6 min-w-0 pr-1 pl-8">
                                            <button
                                              disabled={isLocked}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (!isLocked) toggleEvent(event.event_id);
                                              }}
                                              className={`absolute left-0 top-0 rounded-lg border p-1.5 transition-colors disabled:cursor-not-allowed ${
                                                isDark
                                                  ? 'border-gray-600 text-white hover:bg-gray-700 disabled:text-gray-500'
                                                  : isSepia
                                                    ? 'border-[#cda878] text-white hover:bg-[#8a6433] disabled:text-[#e0c8a2]'
                                                    : 'border-[#ddd9ff] text-[#635bba] hover:bg-[#f5f3ff] disabled:text-[#a4a1bf]'
                                              }`}
                                              aria-label={isExpanded ? 'بستن جزئیات رویداد' : 'نمایش جزئیات رویداد'}
                                            >
                                              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                            </button>
                                            <p className={`max-w-[230px] whitespace-normal break-words text-right font-persian text-sm font-semibold leading-6 ${contentTitleClasses}`}>
                                              {event.event_title}
                                            </p>
                                          </div>

                                          <AnimatePresence initial={false}>
                                            {isExpanded && (
                                              <motion.p
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.2 }}
                                                className={`mt-2 max-w-[230px] overflow-hidden whitespace-pre-wrap break-words text-right font-persian text-xs leading-6 ${contentSubClasses}`}
                                                dir="rtl"
                                              >
                                                {event.event_description}
                                              </motion.p>
                                            )}
                                          </AnimatePresence>
                                        </div>

                                        <div className="relative z-20">
                                          <div
                                            className={`absolute left-1/2 top-[-12px] bottom-1/2 w-[2px] -translate-x-1/2 ${
                                              eventReached ? (isSepia ? 'bg-[#8a5d28]' : 'bg-[#6f63ff]') : 'bg-[#ddd9ff]'
                                            }`}
                                          />
                                          {eventHasOutgoingLine && (
                                            <div
                                              className={`absolute left-1/2 top-1/2 bottom-[-12px] w-[2px] -translate-x-1/2 ${
                                                eventOutgoingReached ? (isSepia ? 'bg-[#8a5d28]' : 'bg-[#6f63ff]') : 'bg-[#ddd9ff]'
                                              }`}
                                            />
                                          )}
                                          <div className={`absolute left-[-8px] top-1/2 z-30 mt-[-1px] h-[2px] w-[7px] ${eventReached ? (isSepia ? 'bg-[#8a5d28]' : 'bg-[#6f63ff]') : 'bg-[#ddd9ff]'}`} />
                                          <div
                                            className={`absolute left-1/2 top-1/2 z-20 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 ${
                                              eventReached
                                                ? (isSepia ? 'border-[#8a5d28] bg-[#8a5d28]' : 'border-[#6f63ff] bg-[#6f63ff]')
                                                : 'border-[#cfcbe6] bg-white'
                                            }`}
                                          />
                                        </div>
                                      </motion.div>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                )}

              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TimelineBottomSheet;


